<script setup>
import { computed } from "vue";
import store from "@/store/index.js";
import BaseText from "@/library/BaseText.vue";
import BaseSheet from "@/library/BaseSheet.vue";
import BaseIcon from "@/library/BaseIcon.vue";
import Button from "@/features/Button.vue";
import EnrollmentOrb from "@/features/enrollment/EnrollmentOrb.vue";
import { useDisplay } from "@/composables/useDisplay.js";

defineEmits(["download-app", "go-to-dashboard"]);

const isPasswordless = computed(
  () => store.getters["authentication/user"]?.is_passwordless
);

const username = computed(() => store.getters["authentication/username"]);

const { isMobile } = useDisplay();
</script>

<template>
  <div class="page-enrollment-ready">
    <BaseText
      variant="body-small-medium"
      class="page-enrollment-ready__tagline"
    >
      <template v-if="isMobile">Last step • Download Cloaked</template>
      <template v-else>Last step • Go to Dashboard</template>
    </BaseText>
    <BaseText
      as="h1"
      :variant="isMobile ? 'headline-3-bold' : 'headline-1-medium'"
      class="page-enrollment-ready__title"
    >
      You're ready to use Cloaked!
    </BaseText>
    <BaseSheet
      class="page-enrollment-ready__username"
      spacing-x="lg"
      spacing-y="md"
    >
      <BaseText
        variant="body-3-regular"
        as="h2"
      >
        <template v-if="isPasswordless">
          Your username is your phone number
        </template>
        <template v-else>Your username</template>
      </BaseText>
      <BaseText
        variant="headline-3-bold"
        as="p"
      >
        {{ username }}
      </BaseText>
    </BaseSheet>
    <BaseSheet
      class="page-enrollment-ready__progress"
      spacing-y="lg"
      spacing-x="lg"
    >
      <section
        class="page-enrollment-ready__step page-enrollment-ready__step--done"
      >
        <BaseIcon
          name="tick-circle-filled"
          class="page-enrollment-ready__step-icon"
        />
        <BaseText
          as="h2"
          variant="headline-5-bold"
          class="page-enrollment-ready__step-title"
        >
          Exposure Removal has begun
        </BaseText>
        <BaseText
          as="h2"
          variant="body-3-regular"
          class="page-enrollment-ready__step-text"
        >
          Keep track of exposures and removal status in the app.
        </BaseText>
      </section>
      <section
        class="page-enrollment-ready__step page-enrollment-ready__step--done"
      >
        <BaseIcon
          name="tick-circle-filled"
          class="page-enrollment-ready__step-icon"
        />
        <BaseText
          as="h2"
          variant="headline-5-bold"
          class="page-enrollment-ready__step-title"
        >
          Identity Monitoring is activated
        </BaseText>
        <BaseText
          as="h2"
          variant="body-3-regular"
          class="page-enrollment-ready__step-text"
        >
          Enjoy real-time alerts if your personal data becomes part of a breach.
        </BaseText>
      </section>
      <section class="page-enrollment-ready__step">
        <template v-if="isMobile">
          <BaseIcon
            name="monitor-filled"
            class="page-enrollment-ready__step-icon"
          />
          <BaseText
            as="h2"
            variant="headline-5-bold"
            class="page-enrollment-ready__step-title"
          >
            Enable notifications in the Cloaked mobile app
          </BaseText>
        </template>
        <template v-else>
          <BaseIcon
            name="monitor-filled"
            class="page-enrollment-ready__step-icon"
          />
          <BaseText
            as="h2"
            variant="headline-5-bold"
            class="page-enrollment-ready__step-title"
          >
            Start Using Cloaked
          </BaseText>
        </template>
      </section>
      <Button
        v-if="isMobile"
        full-width
        size="2xl"
        type="primary"
        class="page-enrollment-ready__cta"
        @click="$emit('download-app')"
        @keydown.enter="$emit('download-app')"
      >
        Download Cloaked
        <BaseIcon name="home" />
      </Button>
      <Button
        v-else
        full-width
        size="2xl"
        type="primary"
        class="page-enrollment-ready__cta"
        @click="$emit('go-to-dashboard')"
        @keydown.enter="$emit('go-to-dashboard')"
      >
        Go to Dashboard
        <BaseIcon name="home" />
      </Button>
    </BaseSheet>
    <Teleport to="#root">
      <div class="page-enrollment-ready__orbs">
        <EnrollmentOrb class="page-enrollment-ready__orbs-top" />
        <EnrollmentOrb class="page-enrollment-ready__orbs-bottom" />
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.page-enrollment-ready {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__tagline {
    border: 1px solid $color-primary-100;
    border-radius: 100px;
    padding: 4px 10px;
  }

  &__title {
    margin-top: 16px;
    width: 100vw;
    text-align: center;
    padding: 0 16px;
  }

  &__username {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    backdrop-filter: blur(13px);
    margin: 16px auto 0;
    width: 100%;
    max-width: 440px;
    border: 1px solid $color-base-15;
    background: radial-gradient(
        57.2% 296.84% at 50.15% 215.79%,
        rgba(97, 190, 247, 0) 0%,
        rgba(77, 0, 255, 0.12) 100%
      ),
      linear-gradient(
        0deg,
        rgba(77, 0, 255, 0.8) -59.6%,
        rgba(41, 150, 250, 0.8) 15.2%,
        rgba(97, 190, 247, 0.8) 98.13%,
        rgba(109, 51, 245, 0.8) 173.84%
      );

    @media screen and (min-width: $screen-lg) {
      margin-top: 32px;
    }
  }

  &__progress {
    margin-top: 16px;
    backdrop-filter: blur(13px);
    width: 100%;
    max-width: 440px;

    @at-root .theme-light & {
      background-color: rgba($color-base-light, 0.15);
    }

    @at-root .theme-dark & {
      background-color: rgba($color-base-dark, 0.15);
    }
  }

  &__step {
    position: relative;
    padding-left: 40px;
    margin-top: 6px;
    padding-bottom: 10px;

    &:before {
      content: "";
      position: absolute;
      left: 11px;
      bottom: 0;
      height: calc(100% - 30px);
      width: 2px;
      background-color: $color-warning;

      @at-root .page-enrollment-ready__step:last-of-type:before {
        display: none;
      }

      @at-root .page-enrollment-ready__step--done:before {
        background-color: $color-ui-safe-zone-green;
      }
    }

    &-icon {
      position: absolute;
      left: 0;
      top: 0;
      font-size: 24px;
      color: $color-warning;

      @at-root .page-enrollment-ready__step--done & {
        color: $color-ui-safe-zone-green;
      }
    }

    &-title {
      padding-top: 2px;
    }

    &-text {
      opacity: 0.7;
      margin-top: 4px;
    }
  }

  &__cta {
    margin-top: 8px;
    background: linear-gradient(179deg, #ffde6b -4.77%, #faa542 94.7%);

    &:focus {
      outline: 1px solid $color-primary-100;
    }
  }

  &__orbs {
    &-top,
    &-bottom {
      position: fixed;
      width: 160vh;
      height: 160vh;
      left: 50%;
      z-index: 999;
      opacity: 0;
    }

    &-top {
      top: 0;
      transform: translate3d(-50%, -65%, 0);
      animation: appear 1.5s 0.2s ease forwards;

      @media all and (min-width: $screen-lg) {
        transform: translate3d(-50%, -60%, 0);
      }
    }

    &-bottom {
      transform: translate3d(-50%, 70%, 0);
      bottom: 0;
      animation: appear 2s 0.4s ease forwards;

      @media all and (min-width: $screen-lg) {
        transform: translate3d(-50%, 70%, 0);
      }
    }
  }
}
</style>
